/** The Reports library from the browser's side (ADR-0212 decision 6b): the catalogue, one report run
 * for dates and filters, and the address of its export.
 *
 * **Thrown, never defaulted to empty.** A failed read rendered as an empty table would say a report
 * has no rows, and the catalogue rendered as an empty list would say the library is empty. Both
 * throw a `Refused` so the screen can show `ReadFailed`.
 *
 * A report is never a filing: nothing here asks for readiness, a verdict or a regime.
 */

import { skipToken, useQuery } from "@tanstack/react-query";

import { api, apiUrl } from "./client";
import { refusalFrom } from "./refusal";
import type { components } from "./schema";

export type ReportCatalogue = components["schemas"]["DataReportCatalogueOut"];
export type CatalogueEntry = components["schemas"]["DataReportCatalogueEntryOut"];
export type DataReport = components["schemas"]["DataReportOut"];
export type ReportColumn = components["schemas"]["DataReportColumnOut"];

/** What a report can be narrowed by. Every field is optional: absent is "all". */
export interface ReportFilters {
  facilityId?: string;
  vendorId?: string;
  scope?: string;
}

export interface ReportRun extends ReportFilters {
  key: string;
  periodStart: string;
  periodEnd: string;
  method: "location" | "market";
}

const keys = {
  catalogue: () => ["data-reports", "catalogue"] as const,
  run: (run: ReportRun) => ["data-reports", "run", run] as const,
};

export function useReportCatalogue() {
  return useQuery({
    queryKey: keys.catalogue(),
    queryFn: async (): Promise<ReportCatalogue> => {
      const { data, error, response } = await api.GET("/api/v1/data-reports", {});
      if (error || !data) throw refusalFrom(response, error, "The list of reports could not be read.");
      return data;
    },
  });
}

/** One report, run. `run` is `undefined` until a report is chosen: no key, no request. */
export function useDataReport(run: ReportRun | undefined) {
  return useQuery({
    queryKey: run ? keys.run(run) : ["data-reports", "run", "none"],
    queryFn: run
      ? async (): Promise<DataReport> => {
          const { data, error, response } = await api.GET("/api/v1/data-reports/{key}", {
            params: {
              path: { key: run.key },
              query: {
                period_start: run.periodStart,
                period_end: run.periodEnd,
                scope2_method: run.method,
                ...(run.facilityId ? { facility_id: run.facilityId } : {}),
                ...(run.vendorId ? { vendor_id: run.vendorId } : {}),
                ...(run.scope ? { scope: run.scope } : {}),
              },
            },
          });
          if (error || !data) throw refusalFrom(response, error, "This report could not be run.");
          return data;
        }
      : skipToken,
  });
}

/** The address of a report's file. A link the browser follows: the response is an attachment. */
export function reportExportUrl(run: ReportRun, format: "csv" | "xlsx"): string {
  const query = new URLSearchParams({
    period_start: run.periodStart,
    period_end: run.periodEnd,
    scope2_method: run.method,
    format,
  });
  if (run.facilityId) query.set("facility_id", run.facilityId);
  if (run.vendorId) query.set("vendor_id", run.vendorId);
  if (run.scope) query.set("scope", run.scope);
  return apiUrl(`/api/v1/data-reports/${encodeURIComponent(run.key)}/export?${query.toString()}`);
}
